import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Sparkles, TrendingUp, Users, Activity, Target, ChevronRight } from 'lucide-react';
import { getSentryGroupPhotos } from '../../utils/supabase';

// ============================================
// TOP 5 GROUPS COMPONENT
// Displays top 5 performing trading groups
// ============================================

interface Top5Group {
  chatId: number;
  groupName: string;
  realizedPnL: number | null;
  unrealizedPnL: number | null;
  activePositions: number | null;
  winRate: number | null;
  totalTrades: number | null;
  traderCount: number | null;
  photoUrl?: string | null;
}

// Container styled to match ListSection
const Container = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 485px;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 300px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  
  svg {
    width: 16px;
    height: 16px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SummaryStats = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  gap: 24px;
  font-size: 12px;
`;

const SummaryStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
`;

const SummaryValue = styled.div`
  color: #10b981;
  font-weight: 600;
  font-size: 14px;
`;

const Content = styled.div<{ $expanded?: boolean }>`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const GroupCard = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const GroupPhoto = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  border: 2px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`;

const RankBadge = styled.div`
  font-size: 18px;
  font-weight: 700;
  min-width: 32px;
  text-align: center;
`;

const GroupName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  flex: 1;
`;

const TotalPnL = styled.div<{ $positive: boolean }>`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  white-space: nowrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 8px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  
  svg {
    width: 12px;
    height: 12px;
    color: rgba(255, 255, 255, 0.4);
  }
`;

const StatValue = styled.span<{ $color?: string }>`
  font-weight: 600;
  color: ${props => props.$color || '#ffffff'};
`;

const PnLBreakdown = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const BreakdownItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BreakdownLabel = styled.span`
  color: rgba(255, 255, 255, 0.4);
`;

const BreakdownValue = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

const ErrorState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #ef4444;
  font-size: 13px;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

// Format helpers
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00';
  }
  const sign = value >= 0 ? '+' : '';
  return `${sign}$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.0%';
  }
  return `${value.toFixed(1)}%`;
};

const getRankEmoji = (rank: number): string => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
};

const getWinRateColor = (winRate: number): string => {
  if (winRate >= 60) return '#10b981';
  if (winRate >= 40) return '#f59e0b';
  return '#ef4444';
};

// Ensure HTTPS URL for images
const ensureHttpsUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  if (url.startsWith('https://')) {
    return url;
  }
  // If no protocol, assume HTTPS
  return `https://${url}`;
};

// Fetch function
const fetchTop5Groups = async (): Promise<Top5Group[]> => {
  const apiUrl = (import.meta.env as any).VITE_SENTRY_BOT_API_URL;
  if (!apiUrl) {
    throw new Error('Sentry Bot API URL not configured');
  }
  
  const response = await fetch(`${apiUrl}/api/leaderboard/top-5-groups`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to fetch top 5 groups' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  // Handle wrapped response
  if (data.success && data.data) {
    return data.data;
  }
  
  // Handle direct response
  return data;
};

const Top5Groups: React.FC = () => {
  const [groups, setGroups] = useState<Top5Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchTop5Groups();
        
        // Fetch group photos from Supabase
        const chatIds = result.map(group => group.chatId);
        let photoMap = new Map<number, string | null>();
        if (chatIds.length > 0) {
          try {
            photoMap = await getSentryGroupPhotos(chatIds);
            console.log('[Top5Groups] Fetched Supabase photos for', photoMap.size, 'groups');
          } catch (error) {
            console.warn('[Top5Groups] Error fetching Supabase photos:', error);
          }
        }
        
        // Merge photos with group data
        const groupsWithPhotos = result.map(group => ({
          ...group,
          photoUrl: photoMap.get(group.chatId) || null
        }));
        
        setGroups(groupsWithPhotos);
      } catch (err: any) {
        console.error('[Top5Groups] Error fetching data:', err);
        setError(err.message || 'Failed to load top 5 groups');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Calculate summary stats with null checks
  const totalPnL = groups.reduce((sum, group) => {
    const realized = group.realizedPnL ?? 0;
    const unrealized = group.unrealizedPnL ?? 0;
    return sum + (realized + unrealized);
  }, 0);
  const totalTrades = groups.reduce((sum, group) => sum + (group.totalTrades ?? 0), 0);
  const avgWinRate = groups.length > 0 
    ? groups.reduce((sum, group) => sum + (group.winRate ?? 0), 0) / groups.length 
    : 0;
  
  const displayedGroups = expanded ? groups : groups.slice(0, 5);
  
  if (loading) {
    return (
      <Container>
        <Header>
          <Title>
            <Sparkles /> Top 5 Groups
          </Title>
        </Header>
        <LoadingState>Loading top groups...</LoadingState>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container>
        <Header>
          <Title>
            <Sparkles /> Top 5 Groups
          </Title>
        </Header>
        <ErrorState>Error: {error}</ErrorState>
      </Container>
    );
  }
  
  if (groups.length === 0) {
    return (
      <Container>
        <Header>
          <Title>
            <Sparkles /> Top 5 Groups
          </Title>
        </Header>
        <EmptyState>No groups found</EmptyState>
      </Container>
    );
  }
  
  return (
    <Container>
      <Header>
        <Title>
          <Sparkles /> Top 5 Groups
        </Title>
        <ExpandButton onClick={() => setExpanded(!expanded)}>
          <ChevronRight style={{ transform: expanded ? 'rotate(90deg)' : 'none' }} />
        </ExpandButton>
      </Header>
      
      {!expanded && (
        <SummaryStats>
          <SummaryStat>
            <SummaryLabel>Total PnL</SummaryLabel>
            <SummaryValue>{formatCurrency(totalPnL)}</SummaryValue>
          </SummaryStat>
          <SummaryStat>
            <SummaryLabel>Total Trades</SummaryLabel>
            <SummaryValue>{totalTrades}</SummaryValue>
          </SummaryStat>
          <SummaryStat>
            <SummaryLabel>Avg Win Rate</SummaryLabel>
            <SummaryValue>{formatPercentage(avgWinRate)}</SummaryValue>
          </SummaryStat>
        </SummaryStats>
      )}
      
      <Content $expanded={expanded}>
        {displayedGroups.map((group, index) => {
          const rank = index + 1;
          const realized = group.realizedPnL ?? 0;
          const unrealized = group.unrealizedPnL ?? 0;
          const totalPnL = realized + unrealized;
          const isPositive = totalPnL >= 0;
          
          return (
            <GroupCard key={group.chatId}>
              <GroupHeader>
                {group.photoUrl ? (
                  <GroupPhoto 
                    src={ensureHttpsUrl(group.photoUrl) || ''} 
                    alt={group.groupName}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      // Hide photo if it fails to load
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : null}
                <RankBadge>{getRankEmoji(rank)}</RankBadge>
                <GroupName>{group.groupName}</GroupName>
                <TotalPnL $positive={isPositive}>
                  {formatCurrency(totalPnL)}
                </TotalPnL>
              </GroupHeader>
              
              <StatsGrid>
                <StatItem>
                  <Target />
                  <span>Win Rate:</span>
                  <StatValue $color={getWinRateColor(group.winRate ?? 0)}>
                    {formatPercentage(group.winRate)}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <Activity />
                  <span>Active:</span>
                  <StatValue>{group.activePositions ?? 0}</StatValue>
                </StatItem>
                <StatItem>
                  <TrendingUp />
                  <span>Trades:</span>
                  <StatValue>{group.totalTrades ?? 0}</StatValue>
                </StatItem>
                <StatItem>
                  <Users />
                  <span>Traders:</span>
                  <StatValue>{group.traderCount ?? 0}</StatValue>
                </StatItem>
              </StatsGrid>
              
              <PnLBreakdown>
                <BreakdownItem>
                  <BreakdownLabel>Realized</BreakdownLabel>
                  <BreakdownValue>{formatCurrency(group.realizedPnL ?? 0)}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem>
                  <BreakdownLabel>Unrealized</BreakdownLabel>
                  <BreakdownValue>{formatCurrency(group.unrealizedPnL ?? 0)}</BreakdownValue>
                </BreakdownItem>
              </PnLBreakdown>
            </GroupCard>
          );
        })}
      </Content>
    </Container>
  );
};

export default Top5Groups;